/**
 * [INPUT]: 依赖 react，依赖 ../scorer 的 ScoreReport/SUBS/tierOf，依赖 ../report/card、copy、tier-colors
 * [OUTPUT]: 对外提供 ResultView 组件（总分+tier+4主类+8子指标+强弱项+建议+保存/分享图片 + 移动端分享卡预览）
 * [POS]: apps/web/components 的结果视图，docs §8 结果展示流；tier 色用内联 hex 规避 Tailwind 动态类 purge
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../../CLAUDE.md
 */
import { useState } from 'react'
import { SUBS, tierOf, type MainCategory, type ScoreReport } from '../scorer'
import { renderCard } from '../report/card'
import { DISCLAIMER } from '../report/copy'
import { tierHex } from '../report/tier-colors'
import { AiReportView } from './AiReportView'
import type { DiagnosisState } from '../api/diagnose'
import { trackEvent } from '../analytics'

interface Props {
  report: ScoreReport
  onRetry: () => void
  /** AI 诊断状态；不传则视为 idle。 */
  diagnosis?: DiagnosisState
  /** 触发 AI 诊断请求（App 状态机实现）。 */
  onRequestDiagnosis?: () => void
}

const MAIN_LABELS: Record<MainCategory, string> = {
  harmony: 'Harmony',
  dimorphism: 'Dimorphism',
  angularity: 'Angularity',
  presentation: 'Presentation',
}
const MAIN_ORDER: MainCategory[] = ['harmony', 'dimorphism', 'angularity', 'presentation']

export function ResultView({ report, onRetry, diagnosis, onRequestDiagnosis }: Props) {
  const [showDetail, setShowDetail] = useState(false)
  const [copied, setCopied] = useState(false)
  const { tier } = tierOf(report.total)
  const accent = tierHex(report.total)

  // 6维映射雷达图数据，完全对齐 verify.html
  const subScores = report.sub_scores
  const getVal = (k: string) => subScores[k] ?? 6.0

  const radarValues = [
    (getVal('harmony_thirds') + getVal('harmony_midface')) / 2, // 面部比例
    getVal('harmony_symmetry'),                                 // 对称性
    getVal('dimorphism_canthal'),                               // 五官和谐
    getVal('presentation_quality'),                             // 肌肤质感
    getVal('angularity_jaw'),                                   // 健康光泽
    (getVal('dimorphism_jawlip') + getVal('angularity_gonial')) / 2, // 年轻态
  ]

  const center = 100
  const maxRadius = 75
  const angleStep = (Math.PI * 2) / 6
  const startAngle = -Math.PI / 2

  const radarPoints = radarValues.map((val, i) => {
    const radius = (val / 8.0) * maxRadius // 满分为 8.0
    const angle = startAngle + i * angleStep
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
    }
  })

  const pointsStr = radarPoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')

  // 基于正态分布 CDF 估算百分比排名
  function calculateRankPercentile(score: number) {
    const mean = 4.0
    const stdDev = 1.6
    const x = score
    const z = (x - mean) / (stdDev * Math.sqrt(2))
    const t = 1.0 / (1.0 + 0.5 * Math.abs(z))
    const erf = 1.0 - t * Math.exp(-z*z - 1.26551223 + t * (1.00002368 + t * (0.37409196 + t * (0.09678418 + t * (-0.18628806 + t * (0.27886807 + t * (-1.13520398 + t * (1.48851587 + t * (-0.82215223 + t * 0.17087277)))))))));
    let cdf = 0.5 * (1.0 + (z >= 0 ? erf : -erf))
    cdf = Math.max(0.01, Math.min(0.999, cdf))
    return Math.round(cdf * 100)
  }

  const rankPercentile = calculateRankPercentile(report.total)
  const reportOffset = 326.72 - (report.total / 8.0) * 326.72

  function download() {
    trackEvent('Save Image', { tier, score: report.total })
    const url = renderCard(report)
    const a = document.createElement('a')
    a.href = url
    a.download = `psl-score-${report.total.toFixed(1)}.png`
    a.click()
  }

  // 融合原生 Web Share API 实现“分享图片”与文本，在移动端拉起系统分享面板，在 PC 端/不支持环境降级为复制链接
  async function shareImage() {
    trackEvent('Share Image', { tier, score: report.total })
    const dataUrl = renderCard(report)
    const shareText = `My PSL facial score is ${report.total.toFixed(1)}, higher than ${rankPercentile}% of people tested. Find out yours at ${window.location.origin}!`

    const copyFallback = async () => {
      try {
        await navigator.clipboard.writeText(shareText)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy text: ', err)
      }
    }

    if (!navigator.share) {
      await copyFallback()
      return
    }

    try {
      const res = await fetch(dataUrl)
      const blob = await res.blob()
      const file = new File([blob], `psl-report-${report.total.toFixed(1)}.png`, { type: 'image/png' })

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'PSL Facial Attractiveness Report',
          text: shareText,
        })
      } else {
        // Fallback: share text/link only
        await navigator.share({
          title: 'PSL Facial Attractiveness Report',
          text: shareText,
          url: window.location.origin,
        })
      }
    } catch (e: any) {
      // AbortError triggers when user cancels the native share sheet. Do NOT download or copy.
      if (e instanceof DOMException && e.name === 'AbortError') {
        console.log('Share cancelled by user.')
      } else {
        await copyFallback()
      }
    }
  }

  return (
    <div className="max-w-md w-full flex flex-col items-center gap-6 mx-auto">
      
      {/* 1. PSL 分享页分析报告卡 (优先默认置顶展示) */}
      <div className="w-full flex flex-col items-center">
        <div className="w-full rounded-[28px] border border-brass/18 bg-[#171513] p-8 flex flex-col items-center justify-between min-h-[540px] relative overflow-hidden shadow-2xl transition-all duration-300 hover:border-brass/35">
          {/* Card spotlight glow */}
          <div className="absolute top-[40px] left-1/2 -translate-x-1/2 w-[220px] h-[220px] pointer-events-none bg-gradient-to-b from-brass/15 to-transparent rounded-full blur-2xl z-0" />

          {/* Header */}
          <div className="text-center font-light text-slate-400 text-sm tracking-[0.4em] indent-[0.4em] mb-6 relative z-10 select-none">
            PSL Analysis Report
          </div>

          {/* Score circle */}
          <div className="relative w-28 h-28 z-10 mb-4 select-none">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
              <defs>
                <linearGradient id="report-circle-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFEAA7" />
                  <stop offset="100%" stopColor="#E8A34D" />
                </linearGradient>
              </defs>
              <circle className="fill-none stroke-slate-800" cx="60" cy="60" r="52" strokeWidth="5"></circle>
              <circle
                className="fill-none stroke-[url(#report-circle-grad)] transition-all duration-800 ease-out"
                cx="60"
                cy="60"
                r="52"
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray="326.72"
                strokeDashoffset={reportOffset}
                style={{ filter: 'drop-shadow(0px 0px 8px rgba(232, 163, 77, 0.45))' }}
              ></circle>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono text-3xl text-slate-100 font-medium leading-none">{report.total.toFixed(1)}</span>
              <span className="font-mono text-[10px] text-brass opacity-75 mt-1.5 font-medium leading-none">/8.0</span>
            </div>
          </div>

          {/* Percentile Rank */}
          <div className="text-sm text-slate-400 text-center mb-6 z-10">
            Higher than <span className="text-slate-100 font-semibold">{rankPercentile}</span>% of samples
          </div>

          {/* Radar Chart */}
          <div className="w-44 h-44 flex items-center justify-center z-10 mb-3 select-none">
            <svg className="w-full h-full overflow-visible" viewBox="0 0 200 200">
              {/* Radar grids */}
              <polygon points="100,25 165,62.5 165,137.5 100,175 35,137.5 35,62.5" className="fill-none stroke-[#3A3229] opacity-60" strokeWidth="0.8" />
              <polygon points="100,55 139,77.5 139,122.5 100,145 61,122.5 61,77.5" className="fill-none stroke-[#3A3229] opacity-40" strokeWidth="0.8" strokeDasharray="2 2" />
              <polygon points="100,77.5 119.5,88.75 119.5,111.25 100,122.5 80.5,111.25 80.5,88.75" className="fill-none stroke-[#3A3229] opacity-20" strokeWidth="0.8" />
              
              {/* Axis lines */}
              <line x1="100" y1="100" x2="100" y2="25" className="stroke-[#3A3229] opacity-30" strokeWidth="0.8" />
              <line x1="100" y1="100" x2="165" y2="62.5" className="stroke-[#3A3229] opacity-30" strokeWidth="0.8" />
              <line x1="100" y1="100" x2="165" y2="137.5" className="stroke-[#3A3229] opacity-30" strokeWidth="0.8" />
              <line x1="100" y1="100" x2="100" y2="175" className="stroke-[#3A3229] opacity-30" strokeWidth="0.8" />
              <line x1="100" y1="100" x2="35" y2="137.5" className="stroke-[#3A3229] opacity-30" strokeWidth="0.8" />
              <line x1="100" y1="100" x2="35" y2="62.5" className="stroke-[#3A3229] opacity-30" strokeWidth="0.8" />
              
              {/* Radar Shape */}
              <polygon
                points={pointsStr}
                className="fill-coral fill-opacity-25 stroke-coral transition-all duration-800 ease-out"
                strokeWidth="2"
                strokeLinejoin="round"
              />

              {/* Dots */}
              {radarPoints.map((p, i) => (
                <circle
                  key={i}
                  cx={p.x.toFixed(1)}
                  cy={p.y.toFixed(1)}
                  r="3.5"
                  className="fill-[#171513] stroke-coral transition-all duration-800 ease-out"
                  strokeWidth="1.5"
                />
              ))}
              
              {/* Labels */}
              <text x="100" y="15" className="fill-[#9EA4B0] text-[9.5px] font-medium" textAnchor="middle">Proportions</text>
              <text x="175" y="58" className="fill-[#9EA4B0] text-[9.5px] font-medium" textAnchor="start">Symmetry</text>
              <text x="175" y="148" className="fill-[#9EA4B0] text-[9.5px] font-medium" textAnchor="start">Feature Harmony</text>
              <text x="100" y="190" className="fill-[#9EA4B0] text-[9.5px] font-medium" textAnchor="middle">Skin Quality</text>
              <text x="25" y="148" className="fill-[#9EA4B0] text-[9.5px] font-medium" textAnchor="end">Jaw Definition</text>
              <text x="25" y="58" className="fill-[#9EA4B0] text-[9.5px] font-medium" textAnchor="end">Structure</text>
            </svg>
          </div>

          {/* Legends */}
          <div className="flex gap-4 text-[11px] text-slate-400 mb-4 z-10 select-none">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-olive"></span>
              <span>Health</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-brass"></span>
              <span>Attractiveness</span>
            </div>
          </div>

          {/* Footer */}
          <div className="w-full border-t border-brass/12 pt-4 flex justify-between items-center z-10 mt-auto select-none">
            <span className="font-mono text-[10px] text-slate-500">pslanalyzer.com</span>
            <div className="w-8 h-8 opacity-75">
              <svg viewBox="0 0 24 24" className="w-full h-full text-brass" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="6" height="6" />
                <rect x="3" y="3" width="4" height="4" fill="currentColor" fillOpacity="0.2" />
                <rect x="16" y="2" width="6" height="6" />
                <rect x="17" y="3" width="4" height="4" fill="currentColor" fillOpacity="0.2" />
                <rect x="2" y="16" width="6" height="6" />
                <rect x="3" y="17" width="4" height="4" fill="currentColor" fillOpacity="0.2" />
                <path d="M 12 4 L 12 8 M 12 12 L 12 12 M 16 12 L 20 12 M 12 16 L 16 16 M 16 16 L 16 20 M 20 20 L 20 20" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* 2. 保存与分享 按钮组 (双色平行分离按钮，保存采用轻奢线框) */}
      <div className="grid grid-cols-2 gap-3 w-full z-10">
        <button
          onClick={download}
          className="rounded-xl border border-slate-700 text-slate-300 font-semibold py-3 hover:border-slate-650 hover:bg-slate-900/20 transition text-sm flex items-center justify-center"
        >
          Save Image
        </button>
        <button
          onClick={shareImage}
          className={`rounded-xl font-semibold py-3 transition shadow-lg flex items-center justify-center text-sm ${
            copied
              ? 'bg-olive text-white shadow-olive/10'
              : 'bg-brass text-slate-950 hover:bg-gold shadow-brass/10'
          }`}
        >
          {copied ? 'Link Copied!' : 'Share Image'}
        </button>
      </div>

      {/* 分割指示线 */}
      <div className="w-full flex items-center gap-3 py-2 select-none">
        <div className="h-[1px] bg-slate-800 flex-grow" />
        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Facial Geometry Analysis / Metrics</span>
        <div className="h-[1px] bg-slate-800 flex-grow" />
      </div>

      {/* 3. 详细特征诊断报告列表 (收纳在下方) */}
      <div className="w-full space-y-5">
        {/* 总分 + tier 原始条形 */}
        <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-6xl font-bold leading-none font-mono" style={{ color: accent }}>
                {report.total.toFixed(1)}
              </div>
              <div className="text-slate-500 text-sm mt-1">/ 8</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold" style={{ color: accent }}>
                {tier}
              </div>
              <div className="text-slate-500 text-xs mt-1">Confidence {(report.confidence * 100).toFixed(0)}%</div>
            </div>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(report.total / 8) * 100}%`, background: accent }} />
          </div>
          {report.confidence < 0.55 && (
            <p className="mt-3 text-xs text-amber">Low confidence — consider retaking the photo</p>
          )}
        </div>

        {/* 4 主类 */}
        <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-5 space-y-3">
          {MAIN_ORDER.map((k) => (
            <div key={k}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300 font-medium">{MAIN_LABELS[k]}</span>
                <span className="text-slate-400 font-mono">{report.main_scores[k].toFixed(1)}/8</span>
              </div>
              <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(report.main_scores[k] / 8) * 100}%`, background: accent }} />
              </div>
            </div>
          ))}
          <button onClick={() => setShowDetail((v) => !v)} className="text-xs text-slate-500 hover:text-slate-300 pt-1">
            {showDetail ? 'Collapse 8 Sub-Indicators' : 'Expand 8 Sub-Indicators'}
          </button>
        </div>

        {/* 8 子指标 + 可解释 delta */}
        {showDetail && (
          <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-5 space-y-2 text-sm">
            {SUBS.map((s) => (
              <div key={s.key} className="flex justify-between">
                <span className="text-slate-400">{s.label}</span>
                <span className="text-slate-300 font-mono">{report.sub_scores[s.key]?.toFixed(1) ?? '-'}/8</span>
              </div>
            ))}
            <div className="border-t border-slate-700 pt-3 mt-2 space-y-1.5">
              <div className="text-xs text-slate-500 mb-1">Explainable Indicators</div>
              {report.deltas.slice(0, 6).map((d) => (
                <div key={d.key} className="flex justify-between text-xs">
                  <span className="text-slate-400">
                    {d.label} <span className="text-slate-500">ideal {d.ideal}</span>
                  </span>
                  <span className={d.impact < 0 ? 'text-terracotta' : 'text-olive'}>
                    {d.impact > 0 ? '+' : ''}
                    {d.impact}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 强弱项 + 建议 (配合精细前缀标记) */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
            <div className="text-xs text-olive font-semibold mb-2">Strengths</div>
            <ul className="space-y-1.5 text-xs text-slate-300">
              {report.strengths.slice(0, 3).map((s) => (
                <li key={s} className="flex gap-1.5 items-start">
                  <span className="text-olive select-none">✓</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
            <div className="text-xs text-terracotta font-semibold mb-2">Areas to Improve</div>
            <ul className="space-y-1.5 text-xs text-slate-300">
              {report.weaknesses.slice(0, 3).map((s) => (
                <li key={s} className="flex gap-1.5 items-start">
                  <span className="text-terracotta select-none">✦</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {report.suggestions.length > 0 && (
          <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
            <div className="text-xs text-slate-500 mb-2">Improvement Suggestions (Natural Methods)</div>
            <ul className="space-y-1.5 text-xs text-slate-300">
              {report.suggestions.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-slate-500">·</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* AI 专家诊断（可选解锁，docs §5） */}
        <AiSection
          diagnosis={diagnosis}
          onRequestDiagnosis={onRequestDiagnosis}
          tier={tier}
          score={report.total}
        />

      </div>

      <p className="text-[11px] text-slate-500 leading-relaxed text-center select-none">{DISCLAIMER}</p>
      <button
        onClick={() => {
          trackEvent('Retest Click', { from: 'result', tier, score: report.total })
          onRetry()
        }}
        className="block mx-auto text-xs text-slate-500 hover:text-slate-300 select-none"
      >
        Score Again
      </button>
    </div>
  )
}

/** AI 专家诊断区：idle 解锁入口 / loading / error / result。 */
function AiSection({
  diagnosis,
  onRequestDiagnosis,
  tier,
  score,
}: {
  diagnosis?: DiagnosisState
  onRequestDiagnosis?: () => void
  tier: string
  score: number
}) {
  const state = diagnosis ?? { status: 'idle' }

  if (state.status === 'result') {
    return <AiReportView report={state.report} />
  }

  if (state.status === 'loading') {
    return (
      <div className="rounded-2xl border border-brass/40 bg-slate-900/60 p-5 text-center">
        <div className="inline-block w-5 h-5 border-2 border-brass border-t-transparent rounded-full animate-spin align-middle" />
        <div className="text-sm text-slate-300 mt-2">AI expert is analyzing your metrics…</div>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
        <div className="text-xs text-terracotta mb-1">AI Diagnosis Failed</div>
        <div className="text-xs text-slate-400 mb-3">{state.message}</div>
        <button
          onClick={onRequestDiagnosis}
          className="text-xs text-brass hover:text-gold border border-slate-700 hover:border-slate-500 rounded-lg px-3 py-1.5 transition"
        >
          Retry
        </button>
      </div>
    )
  }

  // idle (LUME Bespoke Gold Card Style)
  return (
    <div className="rounded-2xl border-none bg-gradient-to-br from-[#C5AA73] to-[#B59A63] p-5 text-slate-950 relative overflow-hidden shadow-lg">
      {/* Metal Reflection overlay */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-white/15 to-transparent blur-[1px]" />
      
      <div className="relative z-10">
        <div className="text-sm font-semibold text-slate-950 mb-1 tracking-wide uppercase">AI Expert Diagnosis</div>
        <p className="text-xs text-[#3D3628] mb-3 leading-relaxed font-medium">
          Based on your anonymized geometric metrics, receive a personalized aesthetic diagnosis and a 7-day improvement plan. Only numerical metrics are sent — never your photo.
        </p>
        <button
          onClick={() => {
            trackEvent('Unlock AI Report', { tier, score })
            onRequestDiagnosis?.()
          }}
          className="w-full rounded-xl bg-slate-950 text-[#F3F4F6] font-semibold py-2.5 hover:bg-[#292420] transition shadow-md"
        >
          Unlock AI Expert Report
        </button>
      </div>
    </div>
  )
}